import { useMemo } from 'react';
import AvItemWithLogoGroup, { IconSize } from '@components/AvItemWithLogoGroup';
import { IconVariant } from '@components/ItemWithLogo';
import { AvTag, Filter, FormatterType, Illustration } from '@fabric-ui/components';
import { flex, shadowInsetBorder, shadowPrimaryBottomLg } from '@fabric-ui/theme';
import { uniqBy } from '@fabric-ui/utils';
import useMitreObj from '@hooks/useMitreObj';
import { Box, List, ListItem, ListItemIcon, Skeleton, SxProps, Typography } from '@mui/material';
import { format } from 'date-fns';
import { getLast3MonthsDate } from '../utils';
import { AgentDetailAttributeProps, AgentDetailMetricProps, AgentDetailSectionProps, AgentJobDescriptionProps } from './types';

// Styles
export const agentWidgetBoxStyle = (cnt: number) =>
  ({
    flex: 1,
    borderRadius: '2px',
    backgroundColor: 'var(--color-surface-base-primary)',
    overflow: 'auto',
    flexShrink: 0,
    flexBasis: 'calc(25% - 16px)',
    minWidth: '345px',
    maxWidth: 'calc(25% - 16px)',
    boxShadow: shadowInsetBorder,
    background:
      cnt > 0 ? 'linear-gradient(180deg, var(--color-surface-base-primary) 50.16%, var(--color-surface-accent-blue-primary) 100%)' : 'none',
    border: '1px solid var(--color-border-base-subdued)',
    '&:hover': {
      boxShadow: shadowPrimaryBottomLg,
      cursor: 'pointer',
    },
  }) as SxProps;

export const agentTableCommonStyle = {
  ...flex.row,
  alignItems: 'baseline',
  background: 'var(--color-surface-base-primary)',
  position: 'relative',
  overflowX: 'hidden',
  '& .react-resizable-handle': {
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover .react-resizable-handle': {
    opacity: 1,
  },
  '&:not(:hover) .showOnHover': {
    opacity: 0,
    display: 'none',
  },
} as SxProps;

export const agentDescriptionStyle = {
  color: 'var(--color-content-base-tertiary)',
  fontSize: '13px',
  p: 2.5,
  pb: 0,
  mb: 1,
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  maxHeight: '70px',
  minHeight: '75px',
} as SxProps;

export const agentRoiWidgetBoxStyle = {
  flex: 1,
  borderRadius: '2px',
  backgroundColor: 'var(--color-surface-base-primary)',
  overflow: 'auto',
  flexShrink: 0,
} as SxProps;

export const initialAgentHeadCells = [
  {
    isKey: true,
    id: 'id',
    label: 'ID',
    hidden: true,
  },
];

// Util Functions
export const getLast24HoursDate = (date: Date): Date => {
  const last24Hours = new Date(date);
  last24Hours.setHours(last24Hours.getHours() - 24);
  return last24Hours;
};

export const getLast7DaysDate = (date: Date): Date => {
  const last7Days = new Date(date);
  last7Days.setDate(last7Days.getDate() - 7);
  return last7Days;
};

export const TimeSavedFormula = (value: number) => value / 60;
export const FTEMultiplierFormula = (value: number) => value / 480; // Time saved / 8 hours
export const DEFAULT_AGENT_DASHBOARD_FILTER = { 'alert.type': [], 'agents.name': [], 'agents.category': [] } as Filter;
export const DEFAULT_AGENT_DASHBOARD_DATE_FILTER = [getLast3MonthsDate(), new Date()];
export const DEFAULT_AGENT_DASHBOARD_FILTER_CONTEXT = {
  filters: DEFAULT_AGENT_DASHBOARD_FILTER,
  dateRange: DEFAULT_AGENT_DASHBOARD_DATE_FILTER,
  typeMap: {},
};

export const flattenToQueryString = (params: Record<string, any>) => {
  const flatParams: Record<string, string> = {};

  Object.keys(params).forEach(key => {
    const value = params[key];
    flatParams[key] = Array.isArray(value) ? JSON.stringify(value) : String(value);
  });

  return new URLSearchParams(flatParams).toString();
};

export const useAlertListHeadCells = () => {
  const { mitreMap } = useMitreObj();

  // To Do : Dynamic all columns from alert table
  return useMemo(() => {
    const headers = [
      {
        isKey: true,
        id: 'id',
        label: 'ID',
        hidden: true,
      },
      {
        isKey: true,
        id: 'key',
        label: 'Alert Id',
        hidden: false,
      },
      {
        hidden: false,
        id: 'title',
        label: 'Alert name',
        isKey: false,
      },
      {
        hidden: false,
        id: 'score',
        label: 'Score',
        isKey: false,
      },
      {
        hidden: false,
        id: 'severity',
        label: 'Severity',
        isKey: false,
      },
      {
        id: 'created',
        label: 'Created',
        type: FormatterType.date,
        style: { whiteSpace: 'nowrap' },
        formatter: values => (values ? format(new Date(values), 'MMM dd, yyyy') : undefined),
      },
      {
        id: 'sources',
        label: 'Data Source',
        type: 'array',
        style: { '&.MuiTableCell-root.MuiTableCell-body': { py: '4px' } },
        formatter: values => (
          <AvItemWithLogoGroup items={values || []} variant={IconVariant.sourcesMapByName} size={IconSize.collapsedXSmall} />
        ),
        hidden: true,
      },
      {
        hidden: false,
        id: 'state',
        label: 'State',
        isKey: false,
      },
      {
        id: 'last_update',
        label: 'Last Update',
        type: FormatterType.date,
        style: { whiteSpace: 'nowrap' },
        formatter: values => (values ? format(new Date(values), 'MMM dd, yyyy') : undefined),
      },
      {
        id: 'tactic',
        label: 'Mitre tactic',
        formatter: values => (values ? mitreMap[values] : undefined),
        hidden: true,
      },
      {
        hidden: false,
        id: 'type',
        label: 'Type',
        isKey: false,
      },
      {
        hidden: true,
        id: 'category',
        label: 'Category',
        isKey: false,
      },
      {
        hidden: false,
        id: 'verdict',
        label: 'Verdict',
        isKey: false,
      },
      {
        hidden: true,
        id: 'first_seen',
        label: 'First seen',
        isKey: false,
        formatter: values => (values ? format(new Date(values), 'MMM dd, yyyy') : undefined),
      },
    ];

    return uniqBy(
      headers.map(header => ({
        ...header,
        hidden: !!header.hidden,
      })),
      f => f.id
    );
  }, [mitreMap]);
};

export const NoDataFoundWidget = () => (
  <Box sx={{ ...flex.colCenter, textAlign: 'center', flex: 1, height: `calc(100% - 50px)`, svg: { width: 'auto', height: 'auto' } }}>
    <Box
      sx={{
        width: '12%',
        maxWidth: 72,
        aspectRatio: '1',
        '& svg': { width: '100% !important', height: '100% !important' },
      }}>
      <Illustration.Small.NoData />
    </Box>
    <Typography variant="h5" sx={{ color: 'var(--color-content-base-primary)', pt: 2, pb: 2, fontSize: 20 }}>
      No Data
    </Typography>
    <div>
      <Typography variant="body1" sx={{ color: 'var(--color-content-base-tertiary)', fontSize: 14, lineHeight: 2 }}>
        There is no data to present. <br /> This agent did not run at the selected time frame.
      </Typography>
    </div>
  </Box>
);

export const AGENT_TILE_SKELETON_ARRAY = Array.from({ length: 4 });

export const AgentDashboardLoadingWidget = () => (
  <Box sx={{ ...flex.col, gap: 2, width: '100%' }}>
    <Box sx={{ ...flex.justifyBetween }}>
      <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
    </Box>
    <Box sx={{ ...flex.col, gap: 2, overflowY: 'auto' }}>
      <Box sx={{ ...flex.row, gap: 2, width: '100%', overflow: 'auto', minHeight: 137 }}>
        {AGENT_TILE_SKELETON_ARRAY.map((_, index) => (
          <Skeleton
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            variant="rectangular"
            sx={{ ...agentRoiWidgetBoxStyle, height: 137, backgroundColor: 'var(--color-surface-interactive-secondary-default)' }}
          />
        ))}
      </Box>
      <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 1 }} />
    </Box>
  </Box>
);

export const AgentDetailMetric = ({ count, title, isLoading }: AgentDetailMetricProps) => (
  <Box sx={{ border: '1px solid var(--color-border-base-primary)', borderRadius: '4px', mt: 3, p: 1.5, flex: 1 }}>
    <Typography
      variant="body2"
      sx={{
        color: 'var(--color-content-base-secondary)',
        fontSize: 14,
        flex: 2,
      }}>
      {title}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: 'var(--color-content-base-primary)',
        fontSize: 16,
        fontWeight: 800,
        flex: 2,
      }}>
      {isLoading ? <Skeleton width={300} /> : Number(count).toLocaleString()}
    </Typography>
  </Box>
);

export const AgentDetailSection = ({ title, description, isLoading }: AgentDetailSectionProps) => (
  <Box sx={{ mt: 3 }}>
    <Typography
      variant="h6"
      sx={{
        color: 'var(--color-content-base-primary)',
        fontSize: 14,
      }}>
      {title}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: 'var(--color-content-base-secondary)',
        fontSize: 13,
      }}>
      {isLoading ? <Skeleton width={300} /> : description}
    </Typography>
  </Box>
);

export const AgentJobDescriptionSection = ({ title, isLoading, actions = [] }: AgentJobDescriptionProps) => (
  <Box sx={{ mt: 3 }}>
    <Typography
      variant="h6"
      sx={{
        color: 'var(--color-content-base-primary)',
        fontSize: 14,
      }}>
      {title}
    </Typography>
    <List disablePadding sx={{ color: 'var(--color-content-base-secondary)', pl: 0.5 }}>
      {(isLoading ? AGENT_TILE_SKELETON_ARRAY : actions).map((action, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <ListItem key={`${action}-${index}`} disablePadding sx={{ gap: 1, fontSize: 13, alignItems: 'start', pb: 0.5 }}>
          <ListItemIcon sx={{ minWidth: 'auto', pt: 0.5 }}>
            <ColorDot color="var(--color-content-base-secondary)" />
          </ListItemIcon>
          {isLoading ? <Skeleton width={300} /> : (action as string)}
        </ListItem>
      ))}
    </List>
  </Box>
);

export const AgentDetailAttribute = ({ attributes = [], title, isLoading }: AgentDetailAttributeProps) => (
  <Box sx={{ ...flex.row, justifyContent: 'space-between', mt: 2.5 }}>
    <Typography
      variant="h6"
      sx={{
        color: 'var(--color-content-base-tertiary)',
        textTransform: 'uppercase',
        flex: 1,
        fontSize: 12,
      }}>
      {title}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: 'var(--color-content-base-secondary)',
        flex: 2,
        fontSize: 14,
      }}>
      {isLoading ? (
        <Skeleton />
      ) : (
        <span style={{ ...flex.row, gap: 4 }}>
          {attributes.map((attr, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <AvTag key={`${attr}-${idx}`} variant="neutral" text={attr} oneLine />
          ))}
        </span>
      )}
    </Typography>
  </Box>
);
const ColorDot = ({ color }: { color: string }) => (
  <svg width={10} height={10} style={{ width: 10, maxHeight: 10, paddingTop: '2px', flexShrink: 0 }} aria-hidden>
    <circle cx={4} cy={4} r={3} fill={color} />
  </svg>
);
